import * as THREE from "three";
import {
  SymbolType,
  isCircleStairsType,
  isCircleSymbolType,
  isDoorType,
  isSlidingDoorType,
  isDoubleDoorType,
  isPolygonSymbolType,
  isRectStairsType,
  isTurnStairsType,
  isSquareSymbolType,
  isBookShelfType,
  isSmartTableType,
  isSvgType,
  isTriangleSymbolType,
  isWindowType,
} from "../types/floorplannerTypes";
import { resolveSmartTableLayout } from "../features/floorplan3d/symbols/dining/smartTableLayout";
import { resolveBookShelfLayout } from "../features/floorplan3d/symbols/livingRoom/bookShelfLayout";
import { getTurnStairsPlanBounds } from "../features/floorplan3d/symbols/primitives/stairs/stairGeometry";

export type SymbolFootprintSnapPoint = {
  symbolId: string;
  position: THREE.Vector2;
};

export type SymbolFootprintSnapResult = SymbolFootprintSnapPoint & {
  distance: number;
};

/**
 * Footprint of a symbol as reported by the property panel, in world units.
 * Returns `null` for symbols that have no rectangular footprint to measure.
 */
export const getSymbolFootprintSize = (
  symbol: SymbolType,
): { width: number; height: number } | null => {
  if (isSvgType(symbol)) return { width: symbol.svgLength, height: symbol.svgHeight };
  if (isSmartTableType(symbol)) {
    const layout = resolveSmartTableLayout(symbol.smartTable);
    return {
      width: layout.tabletop.width,
      height: layout.tabletop.length,
    };
  }
  if (isBookShelfType(symbol)) {
    const layout = resolveBookShelfLayout(symbol.bookShelf);
    return {
      width: layout.bounds.width,
      height: layout.bounds.depth,
    };
  }
  if (isSquareSymbolType(symbol)) {
    return { width: symbol.squareWidth, height: symbol.squareHeight };
  }
  if (isTriangleSymbolType(symbol)) {
    return { width: symbol.triangleWidth, height: symbol.triangleHeight };
  }
  if (isCircleSymbolType(symbol)) {
    return { width: symbol.circleWidth, height: symbol.circleHeight };
  }
  if (isPolygonSymbolType(symbol)) {
    return { width: symbol.polygonWidth, height: symbol.polygonHeight };
  }
  if (isRectStairsType(symbol)) {
    return { width: symbol.rectStairsWidth, height: symbol.rectStairsHeight };
  }
  if (isTurnStairsType(symbol)) {
    const bounds = getTurnStairsPlanBounds(symbol, {
      defaultStepSize: symbol.stairStepSize ?? 0.32,
    });
    return { width: bounds.width, height: bounds.depth };
  }
  if (isCircleStairsType(symbol)) {
    return {
      width: symbol.circleStairsWidth,
      height: symbol.circleStairsWidth,
    };
  }
  if (isWindowType(symbol)) {
    return { width: symbol.windowLength, height: symbol.windowWidth };
  }
  if (isDoorType(symbol) || isSlidingDoorType(symbol) || isDoubleDoorType(symbol)) {
    // Doors swing / slide outside their footprint, so measuring against them would not
    // line up with anything the property panel reports.
    return null;
  }
  return null;
};

/**
 * Corners plus edge midpoints of a symbol's footprint, rotated and translated
 * into world space. These are exactly the extents the property panel reports,
 * so a ruler snapped to two opposite points measures the stated dimension.
 *
 * @param positionOverride - optional world position; defaults to `symbol.position`
 */
export const getSymbolFootprintSnapPoints = (
  symbol: SymbolType,
  positionOverride?: THREE.Vector2,
): SymbolFootprintSnapPoint[] => {
  const size = getSymbolFootprintSize(symbol);
  if (!size) return [];

  const halfWidth = size.width / 2;
  const halfHeight = size.height / 2;
  if (!(halfWidth > 0) || !(halfHeight > 0)) return [];

  const origin = positionOverride ?? symbol.position;
  const rotation = symbol.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const localPoints: [number, number][] = [
    [-halfWidth, -halfHeight],
    [halfWidth, -halfHeight],
    [halfWidth, halfHeight],
    [-halfWidth, halfHeight],
    [0, -halfHeight],
    [halfWidth, 0],
    [0, halfHeight],
    [-halfWidth, 0],
  ];

  return localPoints.map(([x, y]) => ({
    symbolId: symbol.id,
    position: new THREE.Vector2(
      origin.x + x * cos - y * sin,
      origin.y + x * sin + y * cos,
    ),
  }));
};

/**
 * Finds the closest symbol footprint corner/edge midpoint within `tolerance`.
 * Used by the 2D ruler so measurement chains land on the exact dimensions the
 * property panel shows instead of wherever the pointer happened to be.
 */
export const findSymbolFootprintSnapPoint = (
  point: THREE.Vector2,
  symbols: Iterable<SymbolType>,
  tolerance: number,
): SymbolFootprintSnapResult | null => {
  if (!Number.isFinite(tolerance) || tolerance <= 0) return null;

  let best: SymbolFootprintSnapResult | null = null;

  for (const symbol of symbols) {
    for (const candidate of getSymbolFootprintSnapPoints(symbol)) {
      const distance = point.distanceTo(candidate.position);
      if (distance > tolerance) continue;
      if (!best || distance < best.distance) {
        best = { ...candidate, distance };
      }
    }
  }

  return best;
};
